import React, { useCallback, useEffect, useRef, useState, Fragment } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, LoaderIcon, AlertCircleIcon, UsersIcon } from 'lucide-react';
import { degreeCategories, degreeInterestOptions, focusOptions, getSpecializationsForFocus } from '../data/degrees';
import { searchSchools, MatchedSchool } from '../services/matchingApi';
import { MatchedSchoolCard, DidYouKnowBanner } from '../components/MatchedSchoolCard';
import { TrustSignals } from '../components/TrustSignals';
import { useFormState } from '../hooks/useFormState';
import { ProgramContentSection } from '../components/ProgramContentSection';
import { HomeInlineLeadCapture } from '../components/HomeInlineLeadCapture';
import { usePageMeta } from '../hooks/usePageMeta';
import { readMatchParams, resolveMatchPrefill, buildMatchHeadline, readCopyOverrides } from '../utils/matchParams';
export function DegreeListingPage() {
  const {
    category
  } = useParams<{
    category: string;
  }>();
  const navigate = useNavigate();
  const categoryData = degreeCategories.find(c => c.id === category);
  // Anchor and campaign links carry the visitor's context
  // (?degreeInterest=&areaStudy=&mProgram=), which turns this page into a
  // program-specific landing page: it drives the widget, the headline, and the
  // school listings so all three agree. Resolved once — the URL doesn't change
  // while the page is open.
  const [prefill] = useState(() => resolveMatchPrefill(readMatchParams()));
  const [copy] = useState(() => readCopyOverrides());
  const headline = buildMatchHeadline(prefill, categoryData?.name || 'Degree', copy.headline);
  const subhead = copy.subhead || (prefill.programLabel ? `Compare accredited online ${prefill.programLabel.toLowerCase()} programs and find the right fit for your career goals.` : `${categoryData?.description || ''} Compare top accredited programs and find the right fit for your career goals.`);
  usePageMeta(`${headline} | OnlineCollegeGuide`, `Compare accredited online ${(prefill.programLabel || categoryData?.name || 'degree').toLowerCase()} programs. Get matched free by career goals, schedule, and budget.`, {
    ogImage: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=1200&h=630&fit=crop&q=80',
    ogType: 'website'
  });
  const {
    formData,
    errors,
    updateFormData,
    validateHeroForm
  } = useFormState({
    degreeInterest: prefill.degreeInterest,
    areaOfStudy: prefill.areaOfStudy || categoryData?.focusValue || '',
    specialization: prefill.specialization
  });
  const filteredSpecializations = formData.areaOfStudy ? getSpecializationsForFocus(formData.areaOfStudy) : [];
  // API state
  const [matchedSchools, setMatchedSchools] = useState<MatchedSchool[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const handleAreaOfStudyChange = (value: string) => {
    updateFormData('areaOfStudy', value);
    updateFormData('specialization', '');
  };
  const generalSpec = categoryData?.focusValue ? getSpecializationsForFocus(categoryData.focusValue).find(s => s.type.toLowerCase().startsWith('general') || s.type.toLowerCase().startsWith('all')) : undefined;
  // Fetch results from API
  const fetchResults = useCallback(async (degreeInterest: string, areaStudy: string, mProgram: string) => {
    setIsLoading(true);
    setApiError(null);
    setHasSearched(true);
    const result = await searchSchools({
      degreeInterest: degreeInterest || undefined,
      areaStudy: areaStudy || undefined,
      mProgram: mProgram || undefined
    });
    if (result.success) {
      setMatchedSchools(result.schools);
    } else {
      setApiError('Unable to load school matches. Please try again.');
      setMatchedSchools([]);
    }
    setIsLoading(false);
  }, []);
  // Auto-fetch on page load. URL context wins, so the listings match the
  // program named in the headline rather than the whole category.
  useEffect(() => {
    const area = prefill.areaOfStudy || categoryData?.focusValue;
    if (!area) return;
    fetchResults(prefill.degreeInterest, area, prefill.specialization || generalSpec?.value || '');
  }, [categoryData?.focusValue, fetchResults, generalSpec?.value, prefill]);
  // On mobile the search card sits above the listings, so a new search would
  // otherwise leave the visitor looking at the form they just submitted.
  const resultsRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const handleSidebarSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateHeroForm()) {
      pendingScrollRef.current = true;
      fetchResults(formData.degreeInterest, formData.areaOfStudy, formData.specialization);
    }
  };
  useEffect(() => {
    if (isLoading || !pendingScrollRef.current) return;
    const target = resultsRef.current;
    pendingScrollRef.current = false;
    if (!target || window.innerWidth >= 1024) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const raf = requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isLoading]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [category]);
  if (!categoryData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center bg-light px-4" data-id="element-1230" data-mp="172piksxezooz">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1231" data-mp="123lbq8xyq31t">
          Category Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-1232" data-mp="x5jsno0je3qc">
          We couldn't find the degree category you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1233" data-mp="t2esvt7ucg5e">
          Return Home
        </Link>
      </div>;
  }
  return <main className="flex-grow bg-light pb-16" data-id="element-1234" data-mp="egzi3pyjxysz">
      {/* Page Header. The illustrated brand band sits behind the title so the
          category page opens on-brand while the copy stays fully legible. */}
      <div className="relative overflow-hidden border-b border-hairline bg-background-alt py-12 md:py-16" data-id="element-1235" data-mp="4j39p5x01hyk">
        <img src="https://cdn.magicpatterns.com/uploads/k8W2HWVxHiuP3fZeA1oasw/ChatGPT_Image_Sep_4,_2026,_11_35_35_AM_1.png" alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" data-id="element-1236" data-mp="9gv1lzxn942r" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1237" data-mp="nhfob76ojk1j">
          {/* Breadcrumbs */}
          <nav className="flex text-sm text-ink-secondary mb-6" aria-label="Breadcrumb" data-id="element-1238" data-mp="sg91szlyvb1p">
            <ol className="inline-flex items-center space-x-1 md:space-x-3" data-id="element-1239" data-mp="1g63cbaitcnis">
              <li className="inline-flex items-center" data-id="element-1240" data-mp="1g40dc0zonk1x">
                <Link to="/" className="inline-flex items-center hover:text-primary transition-colors" data-id="element-1241" data-mp="1kxv39m5vsv9g">
                  <HomeIcon className="w-4 h-4 mr-2" data-id="element-1242" data-mp="35m719v1p95k8" />
                  Home
                </Link>
              </li>
              <li data-id="element-1243" data-mp="30nm6ib57ve36">
                <div className="flex items-center" data-id="element-1244" data-mp="30m1byliwjv5b">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1245" data-mp="3eeh8r4l7mwxs" />
                  <span className="ml-1 md:ml-2" data-id="element-1246" data-mp="3adxbm10mztbn">Degrees</span>
                </div>
              </li>
              <li aria-current="page" data-id="element-1247" data-mp="2mo31vsbm6kvm">
                <div className="flex items-center" data-id="element-1248" data-mp="2mp5rzy144jah">
                  <ChevronRightIcon className="w-4 h-4 mx-1" data-id="element-1249" data-mp="2hp9os108ovvz" />
                  <span className="ml-1 md:ml-2 text-ink font-medium" data-id="element-1250" data-mp="2hqul9w646owh">
                    {prefill.programLabel || categoryData.name}
                  </span>
                </div>
              </li>
            </ol>
          </nav>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-ink" data-id="element-1251" data-mp="aytoykc09y5o">
            {headline}
          </h1>
          <p className="text-lg text-ink-secondary max-w-3xl" data-id="element-1252" data-mp="608pin4kviia">{subhead}</p>
        </div>
      </div>

      <TrustSignals data-id="element-1253" data-mp="q2d9gx1jzjgx" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-1254" data-mp="q2wkruwp79lz">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1255" data-mp="v0esxj2w5ksx">
          {/* Main Content - Listings */}
          <div ref={resultsRef} id="school-matches" className="lg:w-3/4 order-2 lg:order-1 scroll-mt-20" data-id="element-1256" data-mp="z201wtcb1exu">
            {/* Loading State */}
            {isLoading && <div className="flex flex-col items-center justify-center py-16" data-id="element-1257" data-mp="13ujhfvujyk5a">
                <LoaderIcon className="w-8 h-8 text-navy animate-spin mb-4" data-id="element-1258" data-mp="18rtcbr0pno0i" />
                <p className="text-gray-600 font-medium" data-id="element-1259" data-mp="1dqmong49h8z3">
                  Finding your best school matches...
                </p>
              </div>}

            {/* Error State */}
            {!isLoading && apiError && <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3" data-id="element-1260" data-mp="1mqt0yyo842wo">
                <AlertCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-1261" data-mp="3kj71c48dgg1t" />
                <div data-id="element-1262" data-mp="3okihzk99pqxv">
                  <p className="text-red-800 font-medium" data-id="element-1263" data-mp="3ojz9sv9f6gx1">{apiError}</p>
                  <p className="text-red-600 text-sm mt-1" data-id="element-1264" data-mp="3h0zub0p2x9iy">
                    Try adjusting your search criteria or check back later.
                  </p>
                </div>
              </div>}

            {/* Results */}
            {!isLoading && !apiError && matchedSchools.length > 0 && <div className="space-y-4" data-id="element-1265" data-mp="33934x4espdq9">
                {matchedSchools.map((school, idx) => <Fragment key={(school as Record<string, unknown>).schoolid as string || idx} data-id="element-1266" data-mp="3tfwfvxe8a0z" data-mpi={idx}>
                    <MatchedSchoolCard school={school} index={idx} data-id="element-1267" data-mp="3uzpqme42r569" />
                    {idx === 0 && matchedSchools.length > 1 && <DidYouKnowBanner data-id="element-1268" data-mp="3qxvwkoodwzf2" />}
                  </Fragment>)}
              </div>}

            {/* Empty State */}
            {!isLoading && !apiError && hasSearched && matchedSchools.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-12 text-center" data-id="element-1269" data-mp="2fipchcsatcv5">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4" data-id="element-1270" data-mp="2ajvzrmku5pw1">
                    <AlertCircleIcon className="w-8 h-8 text-gray-400" data-id="element-1271" data-mp="poiu8y1ehmis" />
                  </div>
                  <h3 className="text-xl font-bold text-navy mb-2" data-id="element-1272" data-mp="kqha09s8nh5q">
                    No matches found
                  </h3>
                  <p className="text-gray-500 max-w-md mx-auto" data-id="element-1273" data-mp="yi5nohi4ydot">
                    Try adjusting your degree level or program selection in the
                    sidebar to find more results.
                  </p>
                </div>}
          </div>

          {/* Sidebar - Form */}
          <div className="lg:w-1/4 order-1 lg:order-2" data-id="element-1274" data-mp="13h8jrcg519kn">
            <div className="sticky top-24" data-id="element-1275" data-mp="13it98nnli2bn">
              <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6" data-id="element-1276" data-mp="1hf6so2bowdsr">
                <div className="bg-navy px-6 py-4" data-id="element-1277" data-mp="1dfy0h1wasnzb">
                  <h3 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-1278" data-mp="3j7la70abng8n">
                    Get Matched Fast
                  </h3>
                  <p className="text-gray-300 text-xs text-center mt-1" data-id="element-1279" data-mp="3f7kk2idv1glr">
                    Find the right program for you
                  </p>
                </div>

                <form onSubmit={handleSidebarSubmit} className="p-5 space-y-4" data-id="element-1280" data-mp="393wf0mowicgc">
                  {/* Degree Level */}
                  <div data-id="element-1281" data-mp="2vbgje6ybxgpg">
                    <select id="sidebar-degreeInterest" value={formData.degreeInterest} onChange={e => updateFormData('degreeInterest', e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.degreeInterest ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1282" data-mp="2va5gsagmeezx">
                      <option value="" data-id="element-1283" data-mp="2cfgooodyvz26">Select Degree Level</option>
                      {degreeInterestOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1284" data-mp="2hekxhdwzburz">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.degreeInterest && <p className="mt-1 text-xs text-red-600" data-id="element-1285" data-mp="fkc4ds8yucko">
                        {errors.degreeInterest}
                      </p>}
                  </div>

                  {/* Area of Study */}
                  <div data-id="element-1286" data-mp="93k6noam67lt">
                    <select id="sidebar-areaOfStudy" value={formData.areaOfStudy} onChange={e => handleAreaOfStudyChange(e.target.value)} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer ${errors.areaOfStudy ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1287" data-mp="45r0gv9b9dlj">
                      <option value="" data-id="element-1288" data-mp="rxfvwkhfii6p">Select Area of Study</option>
                      {focusOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1289" data-mp="n2s349fe71mb">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.areaOfStudy && <p className="mt-1 text-xs text-red-600" data-id="element-1290" data-mp="1fmif3wrpoj3b">
                        {errors.areaOfStudy}
                      </p>}
                  </div>

                  {/* Specialization / Program */}
                  <div data-id="element-1291" data-mp="1i76nhod55u2v">
                    <select id="sidebar-specialization" value={formData.specialization} onChange={e => updateFormData('specialization', e.target.value)} disabled={!formData.areaOfStudy} className={`block w-full px-3 py-2.5 text-sm border rounded-md bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed ${errors.specialization ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-navy focus:border-navy'}`} data-id="element-1292" data-mp="1n4gjxnyy7607">
                      <option value="" data-id="element-1293" data-mp="2au10k07mpr6b">
                        {formData.areaOfStudy ? 'Select Program' : 'Select an area of study first'}
                      </option>
                      {filteredSpecializations.map(opt => <option key={opt.value} value={opt.value} data-id="element-1294" data-mp="2ksgvyfdovmlm">
                          {opt.type}
                        </option>)}
                    </select>
                    {errors.specialization && <p className="mt-1 text-xs text-red-600" data-id="element-1295" data-mp="3miotvbevvs0e">
                        {errors.specialization}
                      </p>}
                  </div>

                  <button type="submit" disabled={isLoading} className="w-full h-[52px] px-6 bg-primary hover:bg-primary-hover active:bg-secondary disabled:opacity-50 disabled:pointer-events-none text-white font-semibold rounded-full text-base leading-none shadow-ds-md transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary flex items-center justify-center gap-2" data-id="element-1296" data-mp="3hj2f2xjeq3ph">
                    {isLoading && <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-1297" data-mp="3clk4z3tnlifw" />}
                    {isLoading ? 'Searching...' : 'Search Programs'}
                  </button>
                </form>
              </div>

              {/* Lead capture. The card above filters the listings; this one
                  submits the visitor to the partner APIs, carrying whatever
                  program context is currently selected. */}
              <div className="mb-6" data-id="element-1298" data-mp="vaml70dv8a8v">
                <HomeInlineLeadCapture degreeInterest={formData.degreeInterest} areaStudy={formData.areaOfStudy || categoryData.focusValue} mProgram={formData.specialization || generalSpec?.value || ''} title="Get Matched Free" subtitle="Accredited schools, no obligation" data-id="element-1299" data-mp="zdt0exiyhbrh" />
              </div>

              {/* Undecided nudge — for visitors who can't fill the form yet */}
              <Link to="/degrees" className="group flex items-center gap-3 bg-light hover:bg-white border border-gray-200 rounded-lg px-4 py-3 mb-6 transition-colors duration-200" data-id="element-1300" data-mp="1j3uvqcvwlju8">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-white group-hover:bg-light border border-gray-200 flex items-center justify-center text-navy transition-colors duration-200" data-id="element-1301" data-mp="19a4d3omgk3mg">
                  <UsersIcon className="w-4 h-4" aria-hidden="true" data-id="element-1302" data-mp="21tulz33hd4z8" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-1303" data-mp="2bnqcu5z57po9">
                  <span className="block text-sm font-bold text-navy leading-tight" data-id="element-1304" data-mp="26pf3rxgo9j73">
                    Not sure what to study?
                  </span>
                  <span className="block text-xs text-gray-600 mt-0.5 leading-tight" data-id="element-1305" data-mp="21ohobsn1r39a">
                    Explore top programs by career
                  </span>
                </span>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-200" aria-hidden="true" data-id="element-1306" data-mp="1sq5wxoecw1fs" />
              </Link>

              {/* Related Programs */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1307" data-mp="1izuih7x1q9un">
                <h4 className="font-bold text-navy mb-4 border-b border-gray-100 pb-2" data-id="element-1308" data-mp="1e22qkdweinqg">
                  Programs in {categoryData.name}
                </h4>
                <ul className="space-y-2" data-id="element-1309" data-mp="14z9zaan7yc0m">
                  {getSpecializationsForFocus(formData.areaOfStudy || categoryData.focusValue).map(spec => <li key={spec.value} data-id="element-1310" data-mp="3gmdhxmfol6sf">
                      <button onClick={() => {
                    updateFormData('specialization', spec.value);
                    fetchResults(formData.degreeInterest, formData.areaOfStudy || categoryData.focusValue, spec.value);
                  }} className={`text-sm flex items-center w-full text-left ${formData.specialization === spec.value ? 'text-crimson font-semibold' : 'text-gray-600 hover:text-crimson'}`} data-id="element-1311" data-mp="3lm881nrmtscf">
                        <ChevronRightIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-1312" data-mp="31xr4xrdj0r4d" />
                        {spec.type}
                      </button>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProgramContentSection degreeInterest={formData.degreeInterest} areaOfStudy={formData.areaOfStudy || categoryData.focusValue} specialization={formData.specialization || generalSpec?.value || ''} data-id="element-1313" data-mp="3bpwvcwo4r0hg" />
    </main>;
}